import { type PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm, type UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { TableState } from '../utils';
import { SearchParam, useSavedViewParam } from './SearchParam';
import { getColumnId, TableSessionState, useTableData, useTableName } from '../utils';
import { CircularProgress } from '@nexelem/components';
import { isEqual } from 'lodash';
import { Decoder } from './Decoder';

export const FiltersProvider = ({ children }: PropsWithChildren) => {
  const { tableStateSchema, defaultValues } = useTableData();

  const form = useForm<TableState>({
    defaultValues: defaultValues,
    resolver: zodResolver(tableStateSchema),
    resetOptions: {
      keepDefaultValues: true,
    },
  });

  const { isLoading } = useLoadParamToForm(form);

  useUpdateFormValuesToSearchParams(form, !isLoading);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <CircularProgress />
      </div>
    );
  }

  return <FormProvider {...form}>{children}</FormProvider>;
};

const hiddenColumnIds = (visibility: TableState['visibility'] = {}) =>
  Object.keys(visibility)
    .filter((id) => visibility[id] === false)
    .sort();

const isActuallyEmpty = (obj: TableState, initialColumnOrder: string[], defaultValues: TableState) => {
  if (isEqual(obj, defaultValues)) {
    return true;
  }

  const isOrderingEmpty = obj.ordering === undefined || obj.ordering === null;

  const isSizingEmpty = Object.values(obj.sizing).every(
    (value) => value === undefined || value === null || value === 0,
  );

  const isFiltersEmpty = Object.values(obj.filters).every(
    (value) => value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0),
  );

  const isVisibilityEmpty = isEqual(hiddenColumnIds(obj.visibility), hiddenColumnIds(defaultValues.visibility));

  const isColumnPinningEmpty =
    !obj.columnPinning || ((obj.columnPinning.left?.length ?? 0) === 0 && (obj.columnPinning.right?.length ?? 0) === 0);

  const isColumnOrderEmpty =
    !obj.columnOrder ||
    obj.columnOrder.length === 0 ||
    (obj.columnOrder.length === initialColumnOrder.length &&
      obj.columnOrder.every((id, index) => id === initialColumnOrder[index]));

  return (
    isOrderingEmpty &&
    isSizingEmpty &&
    isFiltersEmpty &&
    isVisibilityEmpty &&
    isColumnOrderEmpty &&
    isColumnPinningEmpty
  );
};

const URL_SYNC_DEBOUNCE_MS = 400;

const useUpdateFormValuesToSearchParams = (form: UseFormReturn<TableState>, enabled: boolean) => {
  const { updateSavedViewParam } = useSavedViewParam();
  const { columns, defaultValues } = useTableData();
  const tableName = useTableName();

  const initialColumnOrder = useMemo(
    () => columns.map((col) => getColumnId(col)).filter((id): id is string => Boolean(id)),
    [columns],
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }

    let syncId = 0;
    let isMounted = true;
    let debounceTimer: ReturnType<typeof setTimeout> | undefined;
    let pendingValues: TableState | undefined;

    const persistSessionOnly = async (values: TableState) => {
      if (isActuallyEmpty(values, initialColumnOrder, defaultValues)) {
        TableSessionState.set(tableName, null);
        return;
      }

      try {
        const encoded = await Decoder.encode(values);
        TableSessionState.set(tableName, encoded);
      } catch {
        // Ignore encode failures during unmount flush.
      }
    };

    const syncToUrlAndSession = async (values: TableState) => {
      const currentSyncId = ++syncId;

      if (isActuallyEmpty(values, initialColumnOrder, defaultValues)) {
        if (!isMounted || currentSyncId !== syncId) {
          return;
        }
        await updateSavedViewParam(null);
        if (!isMounted || currentSyncId !== syncId) {
          return;
        }
        TableSessionState.set(tableName, null);
        return;
      }

      const encoded = await Decoder.encode(values);
      if (currentSyncId !== syncId) {
        return;
      }

      TableSessionState.set(tableName, encoded);

      // Session is keyed by table name (safe after leave). URL must not be written after unmount —
      // React Router may already be on the next route.
      if (!isMounted || encoded === SearchParam.get()) {
        return;
      }

      await updateSavedViewParam(encoded);
    };

    const scheduleSync = (values: TableState) => {
      pendingValues = values;
      if (debounceTimer !== undefined) {
        clearTimeout(debounceTimer);
      }
      debounceTimer = setTimeout(() => {
        debounceTimer = undefined;
        const toSync = pendingValues;
        pendingValues = undefined;
        if (toSync) {
          void syncToUrlAndSession(toSync);
        }
      }, URL_SYNC_DEBOUNCE_MS);
    };

    // Immediate sync after hydrate so URL/session match form even without further edits.
    void syncToUrlAndSession(form.getValues());

    const unsubscribe = form.subscribe({
      formState: {
        values: true,
      },
      callback: ({ values }) => {
        scheduleSync(values);
      },
    });

    return () => {
      unsubscribe();
      isMounted = false;
      syncId += 1;
      if (debounceTimer !== undefined) {
        clearTimeout(debounceTimer);
      }
      pendingValues = undefined;
      // Persist latest form to session only — never touch URL after unmount.
      void persistSessionOnly(form.getValues());
    };
  }, [form, updateSavedViewParam, initialColumnOrder, defaultValues, tableName, enabled]);
};

const useLoadParamToForm = (form: UseFormReturn<TableState>) => {
  const { defaultValues } = useTableData();
  const [isLoading, setIsLoading] = useState(true);

  // biome-ignore lint/correctness/useExhaustiveDependencies: defaultValues should not trigger re-render
  useEffect(() => {
    const loadFn = async () => {
      try {
        const values = await SearchParam.decode();
        form.reset(values ?? defaultValues);
      } catch {
        form.reset(defaultValues);
      } finally {
        setIsLoading(false);
      }
    };
    void loadFn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  return { isLoading };
};
